<template lang="pug">
.FBlogImage(:style="style")
  FRouterLink.FBlogImage__link(
    v-if="link"
    :location="link"
    hide-arrow
  )
    FImage.FBlogImage__image(
      v-bind="image"
      :class="imageClasses"
      width="100%"
      height="100%"
      fit="cover"
    )
    .FBlogImage__imageIcon(v-if="showPlayerIcon")
      svg(
        viewBox="0 0 28 32"
        xmlns="http://www.w3.org/2000/svg"
      )
        path(
          d="M26.3795 13.8482C28.0159 14.816 28.0159 17.184 26.3795 18.1518L4.52266 31.0786C2.85613 32.0643 0.749998 30.863 0.749999 28.9268L0.75 3.07318C0.75 1.137 2.85613 -0.0642836 4.52266 0.921355L26.3795 13.8482Z"
          fill="rgba(255, 255, 255, 0.8)"
        )

  template(v-else)
    FImage(
      v-bind="image"
      width="100%"
      height="100%"
      fit="cover"
    )
  .FBlogImage__caption
    span {{ caption }}
      FRouterLink.FcwBlogImage__link(
        v-if="captionLink.link"
        :location="captionLink.link"
        hide-arrow
      ) {{ captionLink.text }}
</template>

<style lang="stylus">
.FBlogImage
  width var(--FBlogImage--width)
  max-width 100%
  margin 0 auto
  text-align center
  position relative

  caption
    text-align center

.FBlogImage__caption
  display inline-flex
  align-items center
  padding-top rem(16)
  use-font('caption')

  a.FcwBlogImage__link
    use-font('caption')
    text-decoration none
    background-image linear-gradient(var(--color--info), var(--color--info))
    background-position 100% 100%
    background-repeat no-repeat
    background-size 100% rem(2)
    padding rem(0) rem(4)
    transition background-size 200ms ease, border-radius 100ms ease, color 200ms ease
    margin-left rem(4)

    &:hover,
    &:focus
      color var(--color--neutral--light-5)
      background-size 100% 100%
      border-radius rem(4)

.FBlogImage__image::before,
.FBlogImage__image--overlay::before
  content ''
  position absolute
  top 0
  left 0
  width 100%
  height 100%
  transition background 200ms
  z-index 2

.FBlogImage__image:hover::before,
.FBlogImage__image--overlay::before
  background 'rgba(%s, 0.3)' % var(--color--neutral--light-5--rgb)

.FBlogImage__link
  &:hover
    .FBlogImage__image--overlay::before
      background 'rgba(%s, 0.4)' % var(--color--neutral--light-5--rgb)

    .FBlogImage__imageIcon
      transform translate(-50%, -50%) scale(1.05)

.FBlogImage__imageIcon
  position absolute
  left 50%
  top 50%
  transform translate(-50%, -50%)
  background var(--color--primary)
  width rem(56)
  height rem(56)
  border-radius rem(24)
  display flex
  align-items center
  justify-content center
  pointer-events none
  transition transform 200ms

  svg
    width rem(24)
    height rem(24)

.FBlogImage__link
  position relative
</style>

<script setup lang="ts">
import type { FImageProps } from '@fifteen/design-system-vue';
import type { RouteLocationRaw } from 'vue-router';

export interface CaptionLink {
  text?: string;
  link?: RouteLocationRaw;
}

export interface FBlogImageProps {
  /**
   * Image
   */
  image: FImageProps;
  /**
   * Width of image + caption block
   */
  width?: string | number;
  /**
   * Caption of the image
   */
  caption?: string;
  /**
   * Second caption of the image, with a link
   */
  captionLink?: CaptionLink;
  /**
   * Link of the image
   */
  link?: RouteLocationRaw;
  /**
   * Show a media player icon on the overlay. Defaults to false
   */
  showPlayerIcon?: boolean;
}

const props = withDefaults(defineProps<FBlogImageProps>(), {
  width: '100%',
  caption: '',
  captionLink: () => ({}),
  link: '',
  showPlayerIcon: false,
});

const style = computed(
  (): Style => ({
    '--FBlogImage--width': genSize(props.width),
  })
);

const imageClasses = computed(() => ({
  'FBlogImage__image--overlay': props.showPlayerIcon,
}));
</script>
